import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import ActionsButtonMenu from '@src/components/common/buttons/ActionButtonMenu';
import Layout from '@src/components/layout/Layout';

import { Routes } from '@src/navigation/Routes';
import { generatePath, useNavigate } from 'react-router-dom';
import HospitalUsersTable from './compontents/HospitalUsersTable';
import { useCallback, useEffect, useState } from 'react';
import { UserStatusLookUps } from '@src/constants/UserStatus';
import useSearchHospitalUsersLazy from '@src/adapters/hooks/hospital/useSearchHospitalUsers';
import { UserRolesLookUps } from '@src/constants/UserRoles';
import HospitalUserDTO from '@src/adapters/dto/hospital/HospitalUserDTO';
import { StatusUpdate } from '@src/constants/StatusUpdate';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';
import useHospitalDeactivateLazy from '@src/adapters/hooks/hospital/useHospitalDeactivateLazy';
import useHospitalActivateLazy from '@src/adapters/hooks/hospital/useHospitalActivateLazy';
import { enqueueSnackbar } from 'notistack';

const PREFIX = 'HospitalUsers';

const classes = {
  titleBox: `${PREFIX}-pageTitle`,
  styledInput: `${PREFIX}-styledInput`,
};

const StyledLayout = styled(Layout)(({ theme }) => ({
  [`& .${classes.titleBox}`]: {
    marginBottom: '33px',
  },
  [`& .${classes.styledInput}`]: {
    width: '292px',
  },
}));

interface HospitalUsersFilters {
  role: string;
  status: string;
  searchQuery: string;
}

const EMPTY_FILTERS: HospitalUsersFilters = {
  role: '',
  status: '',
  searchQuery: '',
};

function HospitalUsers() {
  const { currentHospital, currentOrganisation } = useNavigationStore();
  const { execute: searchHospitalUsers } = useSearchHospitalUsersLazy();
  const { execute: deactivateHospital } = useHospitalDeactivateLazy();
  const { execute: activateHospital } = useHospitalActivateLazy();
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState(0);
  const [users, setUsers] = useState<HospitalUserDTO[]>([]);

  const navigate = useNavigate();
  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(0);
  };

  const findHospitalUsers = useCallback(async () => {
    const result = await searchHospitalUsers({
      hospitalId: currentHospital?.id ?? '',
      pageSize: rowsPerPage,
      currentPage: currentPage,
      searchText: appliedFilters.searchQuery,
      status: appliedFilters.status,
      role: appliedFilters.role,
    });

    setRecordCount(result.recordCount);
    setUsers(result.records);
  }, [
    searchHospitalUsers,
    currentHospital?.id,
    rowsPerPage,
    currentPage,
    appliedFilters.searchQuery,
    appliedFilters.status,
    appliedFilters.role,
  ]);

  const updateUserStatus = (userId: string, status: StatusUpdate) => {
    findHospitalUsers();
    // setUsers((prev) =>
    //   prev
    //     .map((user) => (user.userId === userId ? { ...user, status } : user))
    //     .filter((user) => user.status !== HospitalRequestStatus.Rejected)
    // );
  };

  useEffect(() => {
    findHospitalUsers();
  }, [findHospitalUsers]);

  return (
    <StyledLayout>
      <Box
        sx={{
          px: 2,
        }}>
        <Stack
          direction={'row'}
          justifyContent="space-between"
          className={classes.titleBox}>
          <Typography variant="h4">{currentHospital?.name}</Typography>

          <Stack direction={'row'} gap={2}>
            <Button variant="outlined" size="large" color="primary">
              Invite Users
            </Button>
            <ActionsButtonMenu
              items={[
                {
                  key: 'edit-hospital',
                  label: 'Edit hospital',
                  to: generatePath(Routes.OrganisationHospitalEdit, {
                    organisationSlug: currentOrganisation?.name ?? '',
                    hospitalSlug: currentHospital?.name ?? '',
                  }),
                },

                ...(currentHospital?.isActive
                  ? [
                      {
                        key: 'deactivate-hospital',
                        label: 'Deactivate hospital',
                        async onClick() {
                          await deactivateHospital({
                            hospitalId: currentHospital.id,
                          });
                          enqueueSnackbar(`Hospital Deactivated`, {
                            variant: 'success',
                          });
                          navigate(-1);
                        },
                      },
                    ]
                  : [
                      {
                        key: 'activate-hospital',
                        label: 'Activate hospital',
                        async onClick() {
                          await activateHospital({
                            hospitalId: currentHospital?.id!,
                          });
                          enqueueSnackbar(`Hospital Activated`, {
                            variant: 'success',
                          });
                          navigate(-1);
                        },
                      },
                    ]),
              ]}
            />
          </Stack>
        </Stack>

        <Stack direction="row" gap={0.75} mb={2}>
          <TextField
            select
            label="Role"
            value={filters.role}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, role: e.target.value }))
            }
            size="small"
            className={classes.styledInput}>
            {UserRolesLookUps.map((regionOption, index) => (
              <MenuItem key={index} value={regionOption.value}>
                {regionOption.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.target.value }))
            }
            size="small"
            className={classes.styledInput}>
            {UserStatusLookUps.map((statusOption, index) => (
              <MenuItem key={index} value={statusOption.value}>
                {statusOption.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            placeholder={'Search name'}
            value={filters.searchQuery}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                searchQuery: e.target.value,
              }));
            }}
            variant="outlined"
            inputProps={{ autoComplete: 'off' }}
            size="small"
            className={classes.styledInput}
          />
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Stack>

        <Paper>
          <HospitalUsersTable
            users={users}
            selectedPage={currentPage}
            rowsPerPage={rowsPerPage}
            recordCount={recordCount}
            changePage={(value: number) => setCurrentPage(value)}
            changeRowsPerPage={(value: number) => {
              setRowsPerPage(value);
              setCurrentPage(0);
            }}
            onUserStatusChange={updateUserStatus}
          />
        </Paper>
      </Box>
    </StyledLayout>
  );
}

export default HospitalUsers;
